import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { formatKobo, type StockRow, type StockView } from '@chicare/shared';

import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { Icon, type IconName } from '@/components/icons/Icon';
import { Card } from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';
import { AdminError } from '@/features/admin/AdminError';

/**
 * The stock room.
 *
 * ══════════════════════════════════════════════════════════════════════════
 *  WHY THIS SCREEN DID NOT EXIST UNTIL NOW
 * ══════════════════════════════════════════════════════════════════════════
 *
 * Everything underneath it was already built. `StockLedger` is the only thing
 * allowed to move stock, every movement carries a type and an actor, every
 * batch records what it COST and when it expires, and goods arrive on a
 * document that refuses short-dated deliveries at the door.
 *
 * And a seller could not look at any of it. They had Products and Orders;
 * there was no way to ask what the shelf is worth, what expires next month,
 * what was lost to damage this quarter, or what is about to run out — which is
 * most of the reason a business keeps records at all. The engine had no dials.
 *
 * ══════════════════════════════════════════════════════════════════════════
 *  WHAT IT IS JUDGED AGAINST
 * ══════════════════════════════════════════════════════════════════════════
 *
 * Not other marketplaces' stock counters. The standard set for this is a
 * business joining ChiCare FOR the inventory — so it is judged against the
 * books a shop already keeps, and it has to be better than the notebook. That
 * means money on every screen, not units: "₦41,200 expiring within 30 days" is
 * a thing somebody acts on this afternoon, and "7 batches" is not.
 *
 * ══════════════════════════════════════════════════════════════════════════
 *  WHY COST, NOT PRICE
 * ══════════════════════════════════════════════════════════════════════════
 *
 * Stock on hand is money already spent, so the headline is what it cost. It is
 * also the only figure that makes a write-off mean anything: a damaged pack
 * costs what it cost, not what it might have sold for. Retail is shown beside
 * it and the gap named as margin — clearly labelled as a projection, because
 * it is money nobody has yet.
 */
export function InventoryScreen() {
  const [view, setView] = useState<StockView>('all');
  const [query, setQuery] = useState('');
  const [tab, setTab] = useState<'stock' | 'movements'>('stock');

  const overview = useQuery({
    queryKey: ['seller', 'inventory'],
    queryFn: ({ signal }) => api.sellerInventory(signal),
  });

  if (overview.isError) {
    return (
      <div className="mx-auto w-full max-w-[1100px] px-4 py-6">
        <AdminError
          title="We couldn't open your stock room"
          error={overview.error}
          onRetry={() => overview.refetch()}
        />
      </div>
    );
  }

  const data = overview.data?.overview;
  const shelf = overview.data?.sector;

  return (
    <div className="mx-auto w-full max-w-[1100px] px-4 py-5 sm:py-6">
      <header className="mb-4">
        <h1 className="m-0 font-display text-[22px] font-bold tracking-[-0.02em] text-ink">
          Inventory
        </h1>
        <p className="m-0 mt-1 max-w-[62ch] text-[12.5px] leading-relaxed text-muted">
          Everything on your shelf, what it cost you, and what needs attention before it
          costs you more.
        </p>
      </header>

      {/* ── WHICH SHELF, STATED — NOT CHOSEN ─────────────────────────
          There was a "Both shelves / Human / Animal" filter here, defaulting
          to both. It was wrong twice over: a business trades on one shelf, so
          there is nothing to choose between — and the default combined two
          businesses' stock into one valuation that described neither.

          The shelf is a fact about the account now, so it is reported rather
          than offered. A business that sells both registers twice and signs
          in to the other one. */}
      {shelf ? (
        <p
          data-testid="inventory-shelf"
          className="mb-4 inline-flex items-center gap-1.5 rounded-full border border-line bg-white px-2.5 py-1 text-[11.5px] font-semibold text-muted"
        >
          <Icon name="store" size={13} strokeWidth={2} />
          {shelf === 'veterinary' ? 'Animal health' : 'Human medicines'}
        </p>
      ) : null}

      {overview.isLoading || !data ? (
        <div className="grid gap-3 sm:grid-cols-3">
          {Array.from({ length: 3 }).map((_, index) => (
            <Skeleton key={index} className="h-[88px] w-full" radius="card" />
          ))}
        </div>
      ) : (
        <>
          {/* ── WHAT IT IS WORTH ─────────────────────────────────────── */}
          <div className="grid gap-3 sm:grid-cols-3">
            <Figure
              label="Stock at cost"
              value={formatKobo(data.value.at_cost_kobo)}
              note={`${data.counts.units.toLocaleString()} units · ${data.counts.skus} products`}
              tone="ink"
            />
            <Figure
              label="If it all sells"
              value={formatKobo(data.value.at_retail_kobo)}
              /* Named as a projection. It is not money anybody has, and a
                 figure like that sitting unqualified next to a real one is
                 how a shop owner plans against revenue that never arrives. */
              note="At today's prices, before ChiCare's share"
              tone="muted"
            />
            <Figure
              label="Margin on the shelf"
              value={`${(data.value.margin_bps / 100).toFixed(1)}%`}
              note={formatKobo(data.value.margin_kobo)}
              tone="good"
            />
          </div>

          {/* ── WHAT NEEDS ATTENTION ─────────────────────────────────── */}
          <h2 className="mb-2 mt-6 font-mono text-[10.5px] uppercase tracking-[0.12em] text-faint">
            Needs attention
          </h2>

          <div className="grid grid-cols-2 gap-2.5 lg:grid-cols-4">
            <Alert
              icon="alert"
              label="Already expired"
              count={data.alerts.expired}
              money={data.alerts.expired_value_kobo}
              tone="danger"
              empty="Nothing expired"
              onOpen={() => {
                setView('expired');
                setTab('stock');
              }}
              active={view === 'expired'}
            />
            <Alert
              icon="clock"
              label="Expires within 30 days"
              count={data.alerts.expiring_30}
              money={data.alerts.expiring_30_value_kobo}
              tone="caution"
              empty="Nothing due in 30 days"
              onOpen={() => {
                setView('expiring');
                setTab('stock');
              }}
              active={view === 'expiring'}
            />
            <Alert
              icon="box"
              label="Running low"
              count={data.alerts.low_stock}
              tone="caution"
              empty="Nothing below its reorder level"
              onOpen={() => {
                setView('low');
                setTab('stock');
              }}
              active={view === 'low'}
            />
            <Alert
              icon="x"
              label="Out of stock"
              count={data.alerts.out_of_stock}
              tone="danger"
              empty="Nothing out of stock"
              onOpen={() => {
                setView('out');
                setTab('stock');
              }}
              active={view === 'out'}
            />
          </div>

          {/* ── WHAT HAS BEEN LOST ───────────────────────────────────── */}
          {data.written_off.units > 0 ? (
            <Card className="mt-4 p-3.5">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h2 className="m-0 text-[13px] font-bold text-ink">
                  Written off in the last {data.written_off.days} days
                </h2>
                <span className="font-mono text-[14px] font-bold text-danger">
                  −{formatKobo(data.written_off.cost_kobo)}
                </span>
              </div>

              {/* Kept apart on purpose: a damaged pack is a handling problem,
                  an expired one is a buying problem, a lost one is a security
                  problem. Totalling them into "shrinkage" is how a business
                  stops being able to act on any of it. */}
              <ul className="m-0 mt-2 grid list-none gap-1.5 p-0 sm:grid-cols-2">
                {data.written_off.by_type.map((row) => (
                  <li
                    key={row.type}
                    className="flex items-baseline justify-between gap-3 rounded-[8px] bg-paper px-2.5 py-1.5"
                  >
                    <span className="text-[12px] font-medium text-ink">{row.label}</span>
                    <span className="shrink-0 text-[11.5px] text-muted">
                      {row.units} units ·{' '}
                      <span className="font-mono font-semibold text-danger">
                        {formatKobo(row.cost_kobo)}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            </Card>
          ) : null}

        </>
      )}

      {/* ── THE SHELF ITSELF ─────────────────────────────────────────── */}
      <div className="mt-6 flex gap-1 border-b border-line">
        {(
          [
            ['stock', 'The shelf'],
            ['movements', 'Every change'],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            data-testid={`inventory-tab-${key}`}
            className={cn(
              'min-h-11 border-b-2 px-3 text-[13px] font-semibold',
              tab === key ? 'border-blue text-ink' : 'border-transparent text-muted hover:text-ink',
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'stock' ? (
        <StockList view={view} onView={setView} query={query} onQuery={setQuery} />
      ) : (
        <MovementList />
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════

function Figure({
  label,
  value,
  note,
  tone,
}: {
  label: string;
  value: string;
  note: string;
  tone: 'ink' | 'muted' | 'good';
}) {
  return (
    <Card className="p-3.5">
      <p className="m-0 font-mono text-[10px] uppercase tracking-[0.1em] text-faint">{label}</p>
      <p
        className={cn(
          'm-0 mt-1 font-display text-[21px] font-bold tracking-[-0.02em]',
          tone === 'good' ? 'text-verified' : tone === 'muted' ? 'text-muted' : 'text-ink',
        )}
      >
        {value}
      </p>
      <p className="m-0 mt-0.5 text-[11px] leading-snug text-faint">{note}</p>
    </Card>
  );
}

/**
 * One thing that needs doing, with what it is worth.
 *
 * A zero state is NOT hidden. "Nothing expired" is information — it is the
 * answer to the question somebody opened this screen to ask — and a tile that
 * disappears when it is clear is a tile somebody wonders whether they imagined.
 */
function Alert({
  icon,
  label,
  count,
  money,
  tone,
  empty,
  onOpen,
  active,
}: {
  icon: IconName;
  label: string;
  count: number;
  money?: number;
  tone: 'danger' | 'caution';
  empty: string;
  onOpen: () => void;
  active: boolean;
}) {
  const clear = count === 0;

  return (
    <button
      type="button"
      onClick={clear ? undefined : onOpen}
      disabled={clear}
      data-testid={`inventory-alert-${label.toLowerCase().replace(/[^a-z]+/g, '-')}`}
      className={cn(
        'min-h-[84px] rounded-card border p-3 text-left',
        clear
          ? 'cursor-default border-line bg-white'
          : active
            ? 'border-blue bg-blue-tint'
            : 'border-line bg-white hover:border-blue',
      )}
    >
      <span
        className={cn(
          'flex items-center gap-1.5 text-[11px] font-semibold',
          clear ? 'text-faint' : tone === 'danger' ? 'text-danger' : 'text-caution-ink',
        )}
      >
        <Icon name={icon} size={13} strokeWidth={2} />
        {label}
      </span>

      {clear ? (
        <span className="mt-1.5 block text-[12px] text-faint">{empty}</span>
      ) : (
        <>
          <span className="mt-1 block font-display text-[20px] font-bold text-ink">{count}</span>
          {/* The money, where there is money to show. "4 batches" is a fact;
              "4 batches, ₦41,200" is a decision. */}
          {money !== undefined && money > 0 ? (
            <span className="block font-mono text-[11.5px] font-semibold text-danger">
              {formatKobo(money)} at risk
            </span>
          ) : (
            <span className="block text-[11px] text-muted">batches</span>
          )}
        </>
      )}
    </button>
  );
}

// ══════════════════════════════════════════════════════════════════════════

const VIEWS: Array<[StockView, string]> = [
  ['all', 'Everything'],
  ['expiring', 'Expiring'],
  ['expired', 'Expired'],
  ['low', 'Running low'],
  ['out', 'Out of stock'],
];

function StockList({
  view,
  onView,
  query,
  onQuery,
}: {
  view: StockView;
  onView: (view: StockView) => void;
  query: string;
  onQuery: (query: string) => void;
}) {
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['seller', 'stock', view, query],
    queryFn: ({ signal }) => api.sellerStock({ view, q: query }, signal),
  });

  return (
    <div>
      <div className="mt-3 flex flex-wrap items-center gap-1.5">
        {VIEWS.map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => onView(key)}
            data-testid={`inventory-view-${key}`}
            className={cn(
              'min-h-9 rounded-full border px-3 text-[12px] font-semibold',
              view === key
                ? 'border-ink bg-ink text-white'
                : 'border-line bg-white text-muted hover:text-ink',
            )}
          >
            {label}
          </button>
        ))}

        <label className="ml-auto flex min-h-9 min-w-[160px] flex-1 items-center gap-1.5 rounded-btn border border-line bg-white px-2.5 sm:max-w-[260px] sm:flex-none">
          <Icon name="search" size={14} className="shrink-0 text-faint" />
          <span className="sr-only">Search your stock</span>
          <input
            value={query}
            onChange={(event) => onQuery(event.target.value)}
            placeholder="Find a product"
            className="min-w-0 flex-1 border-0 bg-transparent py-1 text-[12.5px] text-ink outline-none placeholder:text-faint"
          />
        </label>
      </div>

      {isError ? (
        <div className="mt-3">
          <AdminError title="We couldn't load your stock" error={error} onRetry={() => refetch()} />
        </div>
      ) : isLoading ? (
        <div className="mt-3 space-y-2">
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} className="h-[64px] w-full" radius="card" />
          ))}
        </div>
      ) : (data?.rows.length ?? 0) === 0 ? (
        <p className="mt-6 text-center text-[13px] text-muted">
          {view === 'all'
            ? 'Nothing on the shelf yet. Stock arrives through Deliveries.'
            : 'Nothing here — which is the answer you wanted.'}
        </p>
      ) : (
        <>
          <p className="m-0 mt-3 text-[11.5px] text-muted">
            {data!.rows.length} {data!.rows.length === 1 ? 'batch' : 'batches'} ·{' '}
            <span className="font-mono font-semibold text-ink">
              {formatKobo(data!.valueAtCostKobo)}
            </span>{' '}
            at cost
          </p>

          <ul className="m-0 mt-2 list-none space-y-2 p-0">
            {data!.rows.map((row) => (
              <StockCard key={row.batch_id} row={row} />
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

/**
 * One batch.
 *
 * A card and not a table row, because a vendor reads this on a phone — the
 * screen they actually have behind the counter. A twelve-column table at
 * 390px is a horizontal scrollbar, and a horizontal scrollbar is a column
 * nobody ever sees.
 */
function StockCard({ row }: { row: StockRow }) {
  const state = STATES[row.state];

  return (
    <li
      data-testid={`stock-${row.batch_id}`}
      className="rounded-card border border-line bg-white p-3"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="m-0 truncate text-[13px] font-bold text-ink">
            {row.product ?? 'Unnamed product'}
          </p>
          <p className="m-0 mt-0.5 flex flex-wrap items-center gap-x-2 text-[11px] text-muted">
            {/* Always shown. A row that does not say which shelf it is on is
                a row somebody restocks believing it is the other kind. */}
            {row.batch_no ? <span className="font-mono">{row.batch_no}</span> : null}
            {row.pack_size ? <span>{row.pack_size}</span> : null}
          </p>
        </div>

        <span
          className={cn(
            'shrink-0 rounded-full px-2 py-0.5 text-[10.5px] font-bold',
            state.chip,
          )}
        >
          {state.label}
        </span>
      </div>

      <dl className="m-0 mt-2.5 grid grid-cols-2 gap-x-3 gap-y-1.5 sm:grid-cols-4">
        <Cell label="On hand" value={row.quantity.toLocaleString()} />
        {/* Available, not just quantity: reserved units are spoken for by
            orders already placed, and reordering on the gross figure buys
            stock that is already gone. */}
        <Cell
          label="Can sell"
          value={row.available.toLocaleString()}
          hint={row.reserved > 0 ? `${row.reserved} reserved` : undefined}
        />
        <Cell
          label="Expires"
          value={row.expiry_date ?? '—'}
          hint={
            row.days_to_expiry === null
              ? undefined
              : row.days_to_expiry < 0
                ? `${Math.abs(row.days_to_expiry)} days ago`
                : `in ${row.days_to_expiry} days`
          }
        />
        <Cell label="Worth" value={formatKobo(row.value_at_cost_kobo)} hint="at cost" />
      </dl>
    </li>
  );
}

function Cell({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="min-w-0">
      <dt className="m-0 font-mono text-[9.5px] uppercase tracking-[0.08em] text-faint">
        {label}
      </dt>
      <dd className="m-0 truncate text-[12.5px] font-semibold text-ink">{value}</dd>
      {hint ? <dd className="m-0 truncate text-[10px] text-faint">{hint}</dd> : null}
    </div>
  );
}

const STATES: Record<StockRow['state'], { label: string; chip: string }> = {
  ok: { label: 'In stock', chip: 'bg-paper text-muted' },
  soon: { label: 'Watch', chip: 'bg-paper text-muted' },
  low: { label: 'Low', chip: 'bg-caution-tint text-caution-ink' },
  expiring: { label: 'Expiring', chip: 'bg-caution-tint text-caution-ink' },
  expired: { label: 'Expired', chip: 'bg-danger-tint text-danger' },
  out: { label: 'Out', chip: 'bg-danger-tint text-danger' },
};

// ══════════════════════════════════════════════════════════════════════════

/**
 * Every change to the shelf.
 *
 * This is the part a paper book cannot do. `StockLedger` writes one of these
 * for every single movement, so the list is complete by construction rather
 * than by somebody remembering to write it down.
 */
function MovementList() {
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['seller', 'movements'],
    queryFn: ({ signal }) => api.sellerStockMovements({ days: 90 }, signal),
  });

  if (isError) {
    return (
      <div className="mt-3">
        <AdminError title="We couldn't load the history" error={error} onRetry={() => refetch()} />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="mt-3 space-y-2">
        {Array.from({ length: 4 }).map((_, index) => (
          <Skeleton key={index} className="h-[52px] w-full" radius="card" />
        ))}
      </div>
    );
  }

  if ((data?.length ?? 0) === 0) {
    return (
      <p className="mt-6 text-center text-[13px] text-muted">
        No stock has moved in the last 90 days.
      </p>
    );
  }

  return (
    <ul className="m-0 mt-3 list-none space-y-1.5 p-0" data-testid="movement-list">
      {data!.map((movement) => (
        <li
          key={movement.id}
          className="flex items-start gap-3 rounded-card border border-line bg-white p-2.5"
        >
          <span
            className={cn(
              'mt-px shrink-0 rounded-full px-1.5 py-0.5 font-mono text-[11px] font-bold',
              movement.quantity_delta >= 0
                ? 'bg-verified-tint text-verified'
                : 'bg-danger-tint text-danger',
            )}
          >
            {movement.quantity_delta >= 0 ? '+' : ''}
            {movement.quantity_delta}
          </span>

          <span className="min-w-0 flex-1">
            <span className="block truncate text-[12.5px] font-semibold text-ink">
              {movement.product ?? 'A product'}
            </span>
            <span className="block text-[11px] text-muted">
              {movement.label}
              {movement.batch_no ? ` · ${movement.batch_no}` : ''}
              {movement.by ? ` · ${movement.by}` : ''}
            </span>
            {movement.note ? (
              <span className="block truncate text-[10.5px] text-faint">{movement.note}</span>
            ) : null}
          </span>

          <span className="shrink-0 text-right">
            <span className="block font-mono text-[11.5px] font-semibold text-ink">
              {formatKobo(movement.value_kobo)}
            </span>
            <span className="block text-[10px] text-faint">
              {movement.at ? new Date(movement.at).toLocaleDateString('en-NG') : ''}
            </span>
          </span>
        </li>
      ))}
    </ul>
  );
}
